import { getAuthApiUrl } from './apiConfig';

export type VipPlanType = 'monthly' | 'yearly';

export interface VipPlanBlock {
  plan_type: string;
  amount_inr: string;
  label: string;
  product_id?: number;
}

export interface VipPlansResponse {
  success: boolean;
  data?: {
    monthly: VipPlanBlock;
    yearly: VipPlanBlock;
    currency: string;
    free_return_credits_per_cycle?: number;
  };
  message?: string;
}

export interface CartAddResponse {
  success: boolean;
  message?: string;
  cart_total?: string;
}

export async function fetchVipPlans(token: string): Promise<VipPlansResponse> {
  const res = await fetch(getAuthApiUrl('/api/vip-subscriptions/plans/'), {
    headers: { Authorization: `Token ${token}` },
  });
  return res.json().catch(() => ({ success: false, message: 'Invalid response' }));
}

/** Add VIP plan SKU to cart (pay at checkout with the rest of the order). */
export async function addVipPlanToCart(
  token: string,
  productId: number
): Promise<CartAddResponse> {
  const res = await fetch(getAuthApiUrl('/api/cart/add_item/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify({
      product_id: productId,
      quantity: 1,
      replace_quantity: true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      success: false,
      message: typeof data.message === 'string' ? data.message : 'Could not add to cart',
    };
  }
  return data as CartAddResponse;
}

export async function removeCartProduct(
  token: string,
  productId: number
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(getAuthApiUrl('/api/cart/remove_item/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify({ product_id: productId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      success: false,
      message: typeof data.message === 'string' ? data.message : 'Could not remove from cart',
    };
  }
  return { success: true, message: data.message };
}

/** Legacy: instant mock subscribe (avoid for normal UX). */
export async function subscribeVip(
  token: string,
  planType: VipPlanType
): Promise<{ success: boolean; message?: string; data?: Record<string, unknown> }> {
  const res = await fetch(getAuthApiUrl('/api/vip-subscriptions/subscribe/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify({ plan_type: planType }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      success: false,
      message: typeof data.message === 'string' ? data.message : 'Could not activate VIP',
    };
  }
  return data as { success: boolean; message?: string; data?: Record<string, unknown> };
}
